import { useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteConfirmationDialog } from "./DeleteConfirmationDialog";
import { formatDateForProgram } from "../../../utils/DateUtils";

interface EnrollmentsDataGridProps {
  enrollments: any[];
  coaches: any[];
  editingCoach: Record<string, string>;
  onCoachChange: (enrollmentId: string, coachId: string) => void;
  onCoachSave: (enrollmentId: string) => void;
  onViewDetails: (enrollment: any) => void;
  onEdit: (enrollment: any) => void;
  onDelete: (enrollmentId: string) => void;
  canEdit?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "paid":
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "pay_at_counter":
      return "warning";
    case "failed":
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

export const EnrollmentsDataGrid = ({
  enrollments,
  coaches,
  editingCoach,
  onCoachChange,
  onCoachSave,
  onViewDetails,
  onEdit,
  onDelete,
  canEdit = true,
}: EnrollmentsDataGridProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuEnrollmentId, setMenuEnrollmentId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enrollmentToDelete, setEnrollmentToDelete] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setMenuEnrollmentId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuEnrollmentId(null);
  };

  const handleViewDetails = (enrollment: any) => {
    onViewDetails(enrollment);
    handleMenuClose();
  };

  const handleEdit = (enrollment: any) => {
    onEdit(enrollment);
    handleMenuClose();
  };

  const handleDelete = (enrollment: any) => {
    setEnrollmentToDelete(enrollment);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!enrollmentToDelete) return;
    
    setDeleting(true);
    try {
      await onDelete(enrollmentToDelete.id);
      setDeleteDialogOpen(false);
      setEnrollmentToDelete(null);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setEnrollmentToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: "participantName",
      headerName: "Participant",
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" alignItems="center" spacing={2}>
            {params.value}
        </Stack>
      ),
    },
    {
      field: "parentGuardianName",
      headerName: "Guardian",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "selectedStartDate",
      headerName: "Start Date",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "selectedEndDate",
      headerName: "End Date",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "timeSlot",
      headerName: "Time Slot",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "participantAge",
      headerName: "Age Group",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "programCoach",
      headerName: "Program Coach",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "sessionCoach",
      headerName: "Session Coach",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "enrollmentCoach",
      headerName: "Enrollment Coach",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const e = params.row.originalEnrollment;
        const currentCoachId = params.row.currentCoachId;
        const coachValue = editingCoach[e.id] !== undefined ? editingCoach[e.id] : currentCoachId;
        const hasCoachChanged = editingCoach[e.id] !== undefined && editingCoach[e.id] !== currentCoachId;
        const coachSelectValue =
          coachValue && coaches.some((c) => c.id === coachValue) ? coachValue : "";

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              value={coachSelectValue}
              onChange={(ev) => onCoachChange(e.id, ev.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {coaches.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {`${c.firstName} ${c.lastName}` || c.email}
                </MenuItem>
              ))}
            </TextField>
            {hasCoachChanged && (
              <Tooltip title="Save coach assignment">
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => onCoachSave(e.id)}
                  sx={{ 
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" }
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: "paymentStatus",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value as string)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: "Amount Paid",
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" alignItems="center" spacing={2}>
          {params.value ? `$` + (Number(params.value) + Number(params.row.tax)).toFixed(2) : "N/A"}
        </Stack>
      ),  
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const enrollment = params.row.originalEnrollment;
        return (
          <>
            <IconButton
              aria-label="more options"
              size="small"
              onClick={(event) => handleMenuOpen(event, params.id as string)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && menuEnrollmentId === params.id}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleViewDetails(enrollment)}>
                <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                View Details
              </MenuItem>
              {canEdit
                ? [
                    <MenuItem key="edit-enrollment" onClick={() => handleEdit(enrollment)}>
                      <EditIcon fontSize="small" sx={{ mr: 1 }} />
                      Edit Enrollment
                    </MenuItem>,
                    <MenuItem key="delete-enrollment" onClick={() => handleDelete(enrollment)}>
                      <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      Delete Enrollment
                    </MenuItem>,
                  ]
                : null}
            </Menu>
          </>
        );
      },
    },
  ];

  const rows = enrollments.map((e, index) => {
    const currentCoachId = Array.isArray(e.coachIds) && e.coachIds.length > 0 ? e.coachIds[0] : "";
    return {
      id: e.id || index,
      participantName: e.participantName || "-",
      parentGuardianName: e.parentGuardianName || "-",
      selectedStartDate: e.selectedStartDate 
        ? formatDateForProgram(new Date(e.selectedStartDate).toISOString())
        : "-",
      selectedEndDate: e.selectedEndDate 
        ? formatDateForProgram(new Date(e.selectedEndDate).toISOString())
        : "-",
      timeSlot: e.timeSlot || "-",
      participantAge: e.participantAge || "-",
      programCoach: e.session?.program?.programCoach === null ? "Unassigned" : e.session?.program?.programCoach?.firstName + " " + e.session?.program?.programCoach?.lastName || e.session?.program?.programCoach?.email || "-",
      sessionCoach: e.session?.sessionCoachOrEmployee === null ? "Unassigned" : e.session?.sessionCoachOrEmployee?.firstName + " " + e.session?.sessionCoachOrEmployee?.lastName || e.session?.sessionCoachOrEmployee?.email || "-",
      paymentStatus: e.paymentStatus || "Unknown",
      totalAmount: e.totalAmount || 0,
      tax: e.tax || 0,
      originalEnrollment: e,
      currentCoachId,
    };
  });

  return (
    <>
      <Box sx={{ height: 550, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          getRowId={(row) => row.id}
          pageSizeOptions={[5, 10, 25]}
          density="standard"
          disableRowSelectionOnClick
          disableColumnFilter
          disableDensitySelector
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f8f9fa",
            },
          }}
        />
      </Box>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        participantName={enrollmentToDelete?.participantName || ""}
        loading={deleting}
      />
    </>
  );
};

